<template>
    <modal
        :id="id"
        :size="data._id ? 'full' : 'md'"
        :action-buttons="true"
        :save-action="save"
        :title="$t('Edit role')"
        @shown="shown"
        :delete-action="data._id ? deleteRole : null"
        @hidden="hidden"
    >
        <div class="row">
                <div class="col-12 col-md-6">
                    <div class="form-group">
                        <label>{{ $t('Role') }}</label>
                        <b-form-input v-model.trim="data.role" :placeholder="$t('Ex.:Sales manager')"></b-form-input>
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="form-group">
                        <label>{{ $t('Description') }}</label>
                        <b-form-input
                            v-model.trim="data.description"
                            :placeholder="$t('Ex.:Can approve payments and manage billing')"
                        ></b-form-input>
                    </div>
                </div>
                <div class="col-12">
                    <div class="card mb-0" v-if="data._id">
                        <div class="card-header">
                            <div class="row align-items-center">
                                <div class="col">
                                    <div class="row align-items-center">
                                        <div class="col-auto pr-0">
                                            <span
                                                role="button"
                                                class="fe fe-x text-muted"
                                                v-if="searchQuery"
                                                @click="searchQuery=null"
                                            ></span>
                                            <span class="fe fe-search text-muted" v-else></span>
                                        </div>
                                        <div class="col">
                                            <input
                                                type="search"
                                                v-model="searchQuery"
                                                class="form-control form-control-flush search no-glow"
                                                :placeholder="$t('Search')"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <permissions-table
                            :data="filteredPermissions"
                            :columns="columns"
                            :update-item="updateItem"
                            :bulk-update="bulkUpdate"
                        ></permissions-table>
                    </div>
                </div>
            </div>
    </modal>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import Modal from "../Modal";
import {modalMixins} from '@/mixins/modal.js'
import PermissionsTable from "./PermissionsTable";

export default {
    name: "RoleModal",
    components: {PermissionsTable, Modal},
    props: ['id'],
    mixins: [modalMixins],
    data() {
        return {
            data: {},
            permissions: [],
            searchQuery: "",
            groupedPermissions: {},
            editedPermissions: {},
            createdPermissions: [],
            services: [],
            columns: [
                {
                    label: "Resource",
                    name: "resource",
                    field: item => {
                        return this.$t(`perm.${item.resource}`);
                    }
                },
                {
                    label: "Create",
                    name: "create",
                    checkbox: true,
                    value: item => {
                        return item.create;
                    },
                    disabled: item => {
                        return this.disabledHandler(item, 'create');
                    },
                    children: (key, item) => {
                        return this.childrenChoose(key, item, 'create')
                    },
                },
                {
                    label: "Edit",
                    name: "edit",
                    checkbox: true,
                    sortable: false,
                    value: item => {
                        return item.edit;
                    },
                    disabled: item => {
                        return this.disabledHandler(item, 'edit');
                    },
                    children: (key, item) => {
                        return this.childrenChoose(key, item, 'edit')
                    },
                },
                {
                    label: "Delete",
                    name: "delete",
                    checkbox: true,
                    sortable: false,
                    value: item => {
                        return item.delete;
                    },
                    disabled: item => {
                        return this.disabledHandler(item, 'delete');
                    },
                    children: (key, item) => {
                        return this.childrenChoose(key, item, 'delete')
                    },
                },
                {
                    label: "See all",
                    name: "seeAll",
                    checkbox: true,
                    sortable: false,
                    value: item => {
                        return item.seeAll;
                    },
                    disabled: item => {
                        return this.disabledHandler(item, 'seeAll');
                    },
                    children: (key, item) => {
                        return this.childrenChoose(key, item, 'seeAll')
                    },
                }
            ],
            groups: {
                reports: {
                    action: ['seeAll'],
                    children: [
                        "appointment-reports",
                        "balance-reports",
                        "service-reports",
                        "payment-reports",
                        "customer-reports",
                        "product-reports",
                        "sales-reports",
                        "media-summary",
                        "invoices-summary",
                        "sales-summary",
                        "logs-summary",
                        "toppers",
                        "expense",
                        "payment-history",
                        "expense-reports",
                    ],
                    expense: {
                        action: ['create', 'seeAll']
                    }
                },
                appointment: {
                    action: ['view', 'create', 'edit', 'seeAll'],
                    children: ['work-place', 'appointment']
                },
                roles: {
                    action: ['view', 'create', 'edit', 'delete', 'seeAll'],
                    children: ["roles", "permissions"]
                },
                products: {
                    action: ['view', 'create', 'edit', 'delete'],
                    children: ["products", "brands", "supplies", "suppliers", "categories"]
                },
                'treatment-plan': {
                    action: ['create', 'edit', 'delete'],
                    children: ['configurator', 'treatment-plan'],
                    configurator: {
                        action: ['create', 'edit']
                    }
                },
                notifications: {
                    action: ['view', 'create', 'edit', 'delete', 'seeAll'],
                    children: [
                        "notifications",
                        "my-notifications",
                        "messages",
                        "notification",
                        "customer-notifications",
                        "sms",
                        "push-notifications",
                        "subscriptions",
                        "mailer"
                    ],
                    messages: {
                        hidden: true
                    }
                },
                treatment: {
                    action: ['view', 'create', 'edit', 'delete', 'seeAll'],
                    children: [
                        "treatment",
                        "cdb",
                        "diagnose-objectively",
                        "exam",
                        "icd",
                        "odb",
                        "manipulations",
                        "tooth",
                        "service"
                    ]
                },
                "my-team": {
                    action: ['view', 'create', 'edit', 'delete'],
                    children: ["my-team", "users"]
                },
                customers: {
                    action: ['view', 'create', 'edit', 'delete', 'seeAll'],
                    children: ["customers", "notes", "xrays", "tags", 'communications'],
                    tags: {
                        action: ['create', 'edit', 'delete']
                    },
                    communications: {
                        action: ['create']
                    }
                },
                'feedback-questionnaire': {
                    action: ['create', 'edit', 'delete', 'seeAll'],
                    children: ['feedback-questionnaire', 'feedback-question', 'feedback-answers']
                },
                'my-company': {
                    action: ['view', 'create', 'edit', 'delete', 'seeAll'],
                    children: [
                        "my-company",
                        "company",
                        "documents",
                        "fields",
                        "sources",
                        "tags",
                        "specialty",
                        "resources"
                    ],
                    'my-company': {
                        action: ['edit']
                    }
                },
                payments: {
                    action: ['view', 'create', 'edit', 'delete', 'seeAll'],
                    children: ["invoices", "transactions"]
                },
                tasks: {
                    action: ['view', 'create', 'edit', 'delete', 'seeAll'],
                    children: ['tasks']
                },
                dispatch: {
                    hidden: true,
                    action: ['view'],
                    children: ['dispatch', 'dispatcher']
                },
                'pos-cart': {
                    hidden: true,
                    action: ['view'],
                    children: ['pos-cart']
                },
                labs: {
                    action: ['create', 'edit', 'seeAll'],
                    children: ['labs', 'technicians-service', 'technicians', 'technicians-treatment'],
                    'technicians-service': {
                        hidden: true,
                    },
                    'technicians': {
                        hidden: true,
                    },
                    'technicians-treatment': {
                        hidden: true
                    }
                },
                'force-logout': {
                    action: ['create'],
                    children: ['force-logout']
                },
            }
        };
    },
    methods: {
        ...mapActions("roles", {
            getRoles: "find",
            editRole: "patch",
            createRole: "create",
            removeRole: "remove"
        }),
        ...mapActions("permissions", {
            createPermission: "create",
            editPermission: "patch"
        }),
        ...mapActions("services-list", {fetchServices: "find"}),

        actionHandler(permission, name, value) {
            if (permission.action && !permission.action.includes(name))
                return permission;
            permission[name] = value
            return permission;
        },

        updateItem(value, item, name) {
            Object.keys(this.groupedPermissions).forEach(groupName => {
                const groupIdx = this.groupedPermissions[groupName].findIndex(perm => perm.resource === item.resource);
                let groupedPermission = this.groupedPermissions[groupName][groupIdx];
                if (!groupedPermission)
                    return;

                this.groupedPermissions[groupName][groupIdx] = this.actionHandler(groupedPermission, name, value);

                if (!groupedPermission._id) {
                    const createdPermIdx = this.createdPermissions.findIndex(element => element.resource === groupedPermission.resource);
                    if (this.createdPermissions[createdPermIdx])
                        return;

                    this.createdPermissions.push(this.groupedPermissions[groupName][groupIdx]);
                    return;
                }
                this.editedPermissions[this.groupedPermissions[groupName][groupIdx]._id] = this.groupedPermissions[groupName][groupIdx];
            });

            this.permissions.map(permission => {
                if (permission._id !== item._id) {
                    if (!permission.children)
                        return;
                    return permission.children.map(child => {
                        if (child._id !== item._id)
                            return;
                        return this.actionHandler(child, name, value);
                    });
                }
                return this.actionHandler(permission, name, value);
            });
        },

        bulkUpdate(value, name, children) {
            if (children) {
                children.forEach(child => this.updateItem(value, child, name));
                return;
            }

            this.permissions.forEach(item => {
                this.updateItem(value, item, name);
                if (item.children)
                    item.children.forEach(child => this.updateItem(value, child, name));
            });
        },

        findGroup(resource) {
            // find which of the groups has this resource as it's child
            return Object.keys(this.groups).find(group => this.groups[group].children.find(item => item === resource));
        },

        collectedPermissions() {
            // Collection role from main roles! If new role, we add this to createdPermissions.
            let permissions = this.data.permissions || []
            return this.services.map(service => {
                let existing = permissions.find(permission => permission.resource === service.resource);
                if (existing)
                    return existing
                this.createdPermissions.push(service);
                return service
            });
        },

        configuredPermissions() {
            return Object.keys(this.groupedPermissions).map(groupName => {
                const parentConfig = this.groups[groupName]

                if (this.groupedPermissions[groupName].length > 1) {
                    let actions = parentConfig && parentConfig.action ? parentConfig.action : ['create', 'edit', 'delete', 'seeAll'];

                    let groupItem = this.groupedPermissions[groupName];
                    groupItem = groupItem.map(perm => {
                        if (!parentConfig)
                            return
                        const name = parentConfig.children.find(name => name === perm.resource)
                        const childConfig = parentConfig[name] || {}
                        perm.hidden = childConfig.hidden
                        perm.action = childConfig.action || actions
                        return perm
                    });

                    return {
                        action: actions,
                        resource: groupName,
                        children: groupItem,
                        hidden: this.groups[groupName].hidden
                    };
                }

                let simpleItem = this.groupedPermissions[groupName][0]
                if (this.groups[simpleItem.resource] && this.groups[simpleItem.resource].hidden) {
                    return {
                        ...simpleItem,
                        resource: groupName,
                        hidden: this.groups[simpleItem.resource].hidden,
                    }
                }

                return {...simpleItem, resource: groupName}
            });
        },

        groupPermissions() {
            let permissions = this.collectedPermissions();
            this.groupedPermissions = this.$groupBy(permissions, permission => this.findGroup(permission.resource) || permission.resource);
            this.permissions = this.configuredPermissions();
        },

        shown() {
            const {Role} = this.$FeathersVuex;
            let data = Role.getFromStore('temp');
            if (data) {
                delete data._id;
                this.data = this.$copy(data['model']);
                new Role({
                    model: {},
                    _id: 'temp'
                });
            }

            if (!this.data._id)
                return;
            this.fetchServices({paginate: false}).then(data => {
                this.services = data
                this.groupPermissions()
            });
        },

        save() {
            if (this.data._id) {
                this.editRole([this.data._id, this.data]).then(() => {
                    this.close()
                    this.$toasted.global.success({
                        message: this.$t("Changes to roles saved")
                    });
                }).catch(console.log);
                Object.values(this.editedPermissions).forEach(permission => {
                    this.editPermission([permission._id, permission])
                });
                this.createdPermissions.forEach(permission => {
                    permission.view = true
                    permission.roleId = this.data._id;
                    this.createPermission(permission)
                });
                return
            }
            this.createRole(this.data).then(() => {
                this.close()
                this.$toasted.global.success({
                    message: this.$t("New roles created")
                });
            });
        },
        deleteRole() {
            this.removeRole(this.data._id);
        },

        close() {
            this.$root.$emit("bv::hide::modal", this.id);
        },

        hidden() {
            this.data = {};
            this.editedPermissions = {};
            this.createdPermissions = [];
            this.groupedPermissions = {};
            this.permissions = [];
            this.searchQuery = "";
        },

        disabledHandler(item, action) {
            if (!item.action) return;
            return !item.action.find(x => x === action)
        },
        childrenChoose(key, item, action) {
            let children = item.children.filter(child => !child.hidden);
            let counts = {active: 0, disabled: children.length};
            children.forEach(child => {
                if (child[action])
                    counts.active++
                if (child.action.find(actionName => actionName === action)) {
                    counts.disabled--
                }
            });

            if (counts.active === 0) {
                if (key === 'indeterminate') return false
                if (key === 'determined') return false
            } else if ((counts.active + counts.disabled) === children.length) {
                if (key === 'indeterminate') return false
                if (key === 'determined') return true
            } else {
                if (key === 'indeterminate') return true
                if (key === 'determined') return false
            }
        },
    },
    computed: {
        filteredPermissions() {
            let permissions = this.permissions;
            if (this.searchQuery) {
                permissions = permissions.filter(item => {
                    return this.$t('perm.' + item.resource).match(new RegExp(this.searchQuery, 'i'))
                });
            }
            return permissions;
        }
    }
};
</script>

<style scoped>
</style>
